import { _callBackend, _callFile } from '@/_api/api';
import type { APIRequest, APIResponsePromise } from '@/_types/api';
import type { CompanyForReviewAndExportSerializer } from '@/_types/serializers/v1/get_entities_with_tax_forms';
import type { WorkerStatus } from '@/_types/workerServers';

export const getEntitiesWithTaxForms = async ({
  assigned_advisor_id,
  count,
  export_filter,
  review_required_remaining,
  failed_analysis_filter,
  new_documents,
  selected_entity_tags_filter,
  token,
  ...params
}: APIRequest<{
  assigned_advisor_id: number;
  count?: boolean;
  export_filter?: string;
  review_required_remaining?: boolean;
  failed_analysis_filter?: string;
  new_documents?: boolean;
  selected_entity_tags_filter?: string[];
}>): APIResponsePromise<
  Array<CompanyForReviewAndExportSerializer> & { count: number }
> => {
  const requestParams = {
    ...params,
    assigned_advisor_id,
    count,
    export_filter,
    review_required_remaining,
    failed_analysis_filter,
    new_documents,
    selected_entity_tags_filter,
  };

  return await _callBackend(
    '/review_and_export/get_entities_with_tax_forms',
    'POST',
    requestParams,
    token,
  );
};

export const getWorkerStatus = async ({
  token,
}: APIRequest): APIResponsePromise<{ status: WorkerStatus }> =>
  await _callBackend(
    '/review_and_export/get_worker_status',
    'GET',
    null,
    token,
  );

export const getExportableForms = async ({
  token,
  ...params
}: APIRequest): APIResponsePromise =>
  await _callBackend(
    '/extracted_documents/get_exportable_forms',
    'GET',
    params,
    token,
  );

export const getPreviewAndExtractedDocumentFields = async ({
  token,
  extractedDocumentId,
  companyId,
}: APIRequest<{
  extractedDocumentId: number;
  companyId: number;
}>): APIResponsePromise<{
  preview_link: string;
  extractedDocumentFields?: Array<unknown>;
}> =>
  await _callBackend(
    `/extracted_documents/${extractedDocumentId}/get_preview_and_extracted_document_fields`,
    'POST',
    { company_id: companyId },
    token,
  );

export const updateExtractedDocumentField = async ({
  token,
  companyId,
  extractedDocumentId,
  extractedDocumentFieldId,
  correctedValue,
}: APIRequest<{
  companyId: number;
  extractedDocumentId: number;
  extractedDocumentFieldId: number;
  correctedValue: string;
}>): APIResponsePromise =>
  await _callBackend(
    `/extracted_documents/${extractedDocumentId}/update_extracted_document_field`,
    'POST',
    {
      company_id: companyId,
      extracted_document_field_id: extractedDocumentFieldId,
      corrected_value: correctedValue,
    },
    token,
  );

export const updateExtractedDocument = async ({
  token,
  companyId,
  extractedDocumentId,
  extractedDocumentFields,
  status,
  name,
  documentType,
  reviewStatus,
  year,
}: APIRequest<{
  companyId: number;
  extractedDocumentId: number;
  extractedDocumentFields?: any;
  status?: string;
  name?: string;
  documentType?: string;
  reviewStatus?: string;
  year?: number;
}>): APIResponsePromise =>
  await _callBackend(
    `/extracted_documents/${extractedDocumentId}/update_extracted_document`,
    'POST',
    {
      company_id: companyId,
      extracted_document_fields: extractedDocumentFields,
      document_type: documentType,
      review_status: reviewStatus,
      status,
      name,
      year,
    },
    token,
  );

export const updateDocumentType = async ({
  token,
  companyId,
  extractedDocumentId,
  documentType,
}: APIRequest<{
  companyId: number;
  extractedDocumentId: number;
  documentType: string;
}>): Promise<Response> => {
  return _callBackend(
    `/extracted_documents/${extractedDocumentId}/reassign_tax_document_type`,
    'POST',
    {
      company_id: companyId,
      tax_form_name: documentType,
    },
    token,
    { returnResponseAsIs: true },
  );
};

export const deleteExtractedDocument = async ({
  token,
  id,
}: APIRequest<{ id: number }>): APIResponsePromise =>
  await _callBackend(
    `/extracted_documents/${id}/delete_extracted_document`,
    'GET',
    {},
    token,
  );

export const downloadLeadsheet = async ({
  token,
  companyId,
  year,
  companyName,
  customLeadsheet,
}: APIRequest<{
  companyId: number;
  year: number;
  companyName: string;
  customLeadsheet: boolean;
}>): APIResponsePromise => {
  return _callFile(
    '/extracted_documents/download_leadsheet',
    'POST',
    'application/vnd.ms-excel',
    customLeadsheet
      ? `${companyName} - Custom Leadsheet.xlsx`
      : `${companyName} - Leadsheet.xls`,
    token,
    { company_id: companyId, year, custom_leadsheet: customLeadsheet },
  );
};

export const downloadExportFile = async ({
  token,
  companyId,
  year,
  companyName,
}: APIRequest<{
  companyId: number;
  year: number;
  companyName: string;
}>): APIResponsePromise => {
  return _callFile(
    '/extracted_documents/generate_dat_file_for_scheduled_task_for_entity',
    'POST',
    'application/dat',
    `${companyName} - Tax Export ${year}.dat`,
    token,
    { company_id: companyId, year },
  );
};

export const getSupportedTaxFormsForDataEntry = async ({
  token,
  taxSoftware,
}: APIRequest<{ taxSoftware: string }>): APIResponsePromise<Array<string>> =>
  await _callBackend(
    `/extracted_documents/get_supported_tax_forms_for_data_entry?tax_software=${taxSoftware}`,
    'GET',
    {},
    token,
  );

export const startExportForEntity = async ({
  token,
  companyId,
  year,
}: APIRequest<{
  companyId: number;
  year: number;
}>): APIResponsePromise =>
  await _callBackend(
    '/export_tasks/start_export_for_entity',
    'POST',
    {
      company_id: companyId,
      year,
    },
    token,
  );

export const retryTextractJob = async ({
  token,
  id,
}: APIRequest<{ id: number }>): APIResponsePromise =>
  await _callBackend(
    `/extracted_documents/${id}/retry_textract_job`,
    'POST',
    {},
    token,
  );
